import { Icon } from "@mui/material";
import SessionsApi from "Classes/API/Administration/SessionsApi";
import SearchStringOrIntReq from "Classes/Models/Requests/StringIntSearchReq";
import AddmissionButton from "layouts/AccountingSessions/AddmissionButton";
import Swal from "sweetalert2";
import { useContext, useState } from "react";
import { UserContext } from "App";
import { useNavigate, useLocation } from "react-router-dom";
import { Cached } from "@mui/icons-material";
import PackageStatistics from "../../../AccountingSessions/Packages/PackageDetails/PackageStatistics";
import AppointmentsApi from "Classes/API/Appointments/AppointmentsApi";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SearchUserReq from "../../../../Classes/Models/Requests/Users/SearchUser";
import UsersApi from "../../../../Classes/API/UsersApi";
import EditPartitionButton from "./EditPartitionButton";
import EditBuildingButton from "./EditBuildingButton";
import { useTenant } from "../../../../context/TenantContext";
import SessionControls from "./SessionControls";

const COMMON_ROLES = ["Admin", "Owner", "HeadAccounting"];

const ALLOWED_ADD_ENTRY_ROLES = ["Therapist", ...COMMON_ROLES];

const ALLOWED_HISTORY_ROLES = [
  "SuperAdmin",
  "Therapist",
  "AppointmentEmployee",
  "CallCenterEmployee",
  ...COMMON_ROLES,
];

const ALLOWED_WHATSAPP_ROLES = [
  "SuperAdmin",
  "AppointmentEmployee",
  "CallCenterEmployee",
  "AccountingEmployee",
  ...COMMON_ROLES,
];

const AccSessionDiv = ({
  AccSession,
  EnableFacilityModal,
  GoToAccSessionDetails,
  DeleteAppointment,
  setMovingSession,
  setIsLoaded,
  AppHour,
  isDummy,
  AppDay,
  GetAppDayById,
}) => {
  const navigate = useNavigate();
  const user = useContext(UserContext);
  const [AccountingSession, setAccountingSession] = useState(AccSession);
  const [packageData, setPackageData] = useState(null);
  const location = useLocation();
  const { features } = useTenant();
  const isTrackerEnabled = features.find(
    (f) => f.name === "Doctor_Session_Tracker",
  )?.status;

  const canAddEntry =
    !isDummy && ALLOWED_ADD_ENTRY_ROLES.includes(user?.userType);
  const showPatientHistory =
    AccSession.sessionName !== "Message" &&
    ALLOWED_HISTORY_ROLES.includes(user?.userType);
  const showWhatsapp =
    AccSession.sessionName !== "Message" &&
    ALLOWED_WHATSAPP_ROLES.includes(user?.userType);

  const handleStart = async (id) => {
    if (AccSession.isAdmitted) return;

    Swal.fire({
      title: "Starting Session...",
      text: "Admitting patient and starting timer",
      icon: "info",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      let req = new SearchStringOrIntReq();
      req.SearchInt = id;
      let res = await SessionsApi.ToggleAccountingSessionAdmission(req);
      if (res.status === "Success") {
        Swal.fire(
          "Started",
          "Session is now active and patient is admitted.",
          "success",
        );
        if (setIsLoaded) setIsLoaded(false);
        GetAppDayById(AppDay.id);
      } else {
        Swal.fire("Failed", res.message, "error");
      }
    } catch (error) {
      Swal.fire("Error", "Could not start session", "error");
    }
  };

  const handleEnd = async (id) => {
    Swal.fire({
      title: "End Session?",
      text: "Are you sure you want to end this patient's session?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, End Now",
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Processing...",
          didOpen: () => Swal.showLoading(),
        });

        try {
          let req = new SearchStringOrIntReq();
          req.SearchInt = id;

          const res = await SessionsApi.EndDoctorSession(req);

          if (res.status === "Success") {
            Swal.fire(
              "Completed",
              "Session end time has been recorded.",
              "success",
            );
            if (setIsLoaded) setIsLoaded(false);
            GetAppDayById(AppDay.id);
          } else {
            Swal.fire("Error", res.message, "error");
          }
        } catch (error) {
          console.error(error);
          Swal.fire("Error", "Server connection failed", "error");
        }
      }
    });
  };

  const updateUrlAndNavigate = (path, extraState = {}) => {
    const searchParams = new URLSearchParams(location.search);
    const targetId = "AccSessionDiv" + AccSession.id;
    searchParams.set("targetId", targetId);

    window.history.replaceState(
      null,
      "",
      `${location.pathname}?${searchParams.toString()}`,
    );

    if (path && path !== "#") {
      navigate(path, {
        state: {
          ...location.state,
          ...extraState,
        },
      });
    }
  };

  let DivColor = "badge-dark";

  if (isDummy) {
    DivColor = "badge-secondary";
  } else {
    if (AccSession.isAdmitted) {
      DivColor = "badge-success";
    } else if (AccSession.isAbsent) {
      DivColor = "badge-danger";
    } else if (!AccSession.isPaidFor) {
      DivColor = "badge-warning";
    } else if ((AccSession.packageRefundableAmount || 0) < 0) {
      DivColor = "badge-info";
    }
  }

  const dummyStyle = isDummy
    ? {
        border: "2px dashed #6c757d",
        backgroundColor: "#d7dce0ff",
        color: "#333",
      }
    : {};

  const SendWhatsapp = async ({ patientId, AppHour, AccSession }) => {
    let req = new SearchUserReq();
    req.patientId = patientId;
    let res = await UsersApi.SearchUsers(req);

    if (res.status === "Success") {
      let patient = res.data.list[0];
      let phone = patient.number.replace(/[^\d]/g, "");
      let sessionName = AccSession.sessionName.split(" -")[0];

      let appointmentDate = new Date(AppHour.appointmentHourTime);
      let hours = appointmentDate.getHours();
      let minutes = appointmentDate.getMinutes().toString().padStart(2, "0");
      let ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;

      let dateString = appointmentDate.toLocaleDateString();

      let message = `Dear ${patient.name}, this is an automated message from Elagi Center to confirm your appointment at ${hours}:${minutes} ${ampm} on ${dateString} with ${AccSession.therapistName} for ${sessionName}. Please let us know at least 24 hours in advance if you want make any changes. Thank you.`;

      let url = `https://web.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(
        message,
      )}`;
      window.open(url, "_blank");
    } else {
      Swal.fire("Failed", res.message, "error");
    }
  };

  const ToggleAbsentButtonClick = async (SessionId) => {
    const req = new SearchStringOrIntReq();
    req.SearchInt = SessionId;
    const res = await SessionsApi.ToggleAccountingSessionIsAbsent(req);

    if (res.status === "Success") {
      Swal.fire("Success", res.message, "success");
      setAccountingSession(res.data);
      if (setIsLoaded) setIsLoaded(false);
    } else {
      Swal.fire("Failed", res.message, "error");
    }
  };

  const refreshSessionsRemaining = async () => {
    Swal.fire({
      title: "Refreshing Sessions Remaining",
      icon: "info",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });
    const req = new SearchStringOrIntReq();
    req.SearchInt = AccSession.id;

    const res = await AppointmentsApi.RefreshSessionsRemaining(req);
    if (res.status === "Success") {
      Swal.fire("Success", res.message, "success");
      setAccountingSession(res.data);
      if (setIsLoaded) setIsLoaded(false);
    } else {
      Swal.fire("Failed", res.message, "error");
    }
  };

  function routeToCreateEntry(userId, accSessionId, therapistId) {
    updateUrlAndNavigate("../CreateEntry/" + userId, {
      accSessionId: accSessionId,
      therapistId: therapistId,
    });
  }

  function routeToPatientHistory(userId) {
    updateUrlAndNavigate("/PatientHistory/" + userId, {
      patient: { name: AccSession.patientName, id: userId },
    });
  }

  async function ShowPackageStatistics(packageId) {
    try {
      Swal.fire({
        title: "Loading Package Statistics",
        icon: "info",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const req = new SearchStringOrIntReq();
      req.SearchInt = packageId;
      const res = await SessionsApi.GetPackageById(req);

      Swal.close();
      setPackageData(res.data);
    } catch (error) {
      Swal.fire("Error", "Failed to load package data", "error");
    }
  }

  if (AccSession.sessionName === "Message") {
    return (
      <div
        id={"AccSessionDiv" + AccSession.id}
        className="AppHourSessionDiv"
        style={{
          minHeight: "auto",
        }}
      >
        <div
          style={{
            position: "relative",
            padding: "14px 18px",
            wordBreak: "break-word",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginBottom: "6px",
              opacity: 0.85,
            }}
          >
            <Icon style={{ fontSize: "16px" }}>chat</Icon>
            <span
              style={{
                fontSize: "0.7rem",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              Note
            </span>
          </div>

          <div
            style={{
              fontSize: "1em",
              fontWeight: "600",
              lineHeight: "1.4",
            }}
          >
            {AccSession.patientName}
          </div>

          <div
            style={{
              marginTop: "8px",
              fontSize: "0.65rem",
              opacity: 0.7,
              textAlign: "right",
            }}
          >
            ID: {AccSession.id}
          </div>
        </div>

        {user?.userType !== "Therapist" && (
          <div
            style={{
              display: "flex",
              gap: "6px",
              marginTop: "6px",
              marginLeft: "8px",
            }}
          >
            <button
              className="btn btn-sm"
              onClick={() =>
                setMovingSession({ ...AccSession, moveAction: "cut" })
              }
              title="Cut Message"
              style={{
                background: "#fffaf0",
                border: "1px solid #feebc8",
                borderRadius: "20px",
                padding: "4px 12px",
                fontSize: "0.75rem",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                color: "#dd6b20",
                cursor: "pointer",
              }}
            >
              <Icon style={{ fontSize: "14px" }}>content_cut</Icon>
              Cut
            </button>
            <button
              className="btn btn-sm"
              onClick={() =>
                setMovingSession({ ...AccSession, moveAction: "copy" })
              }
              title="Copy Message"
              style={{
                background: "#f0f0f0",
                border: "1px solid #ddd",
                borderRadius: "20px",
                padding: "4px 12px",
                fontSize: "0.75rem",
                display: "flex",
                alignItems: "center",
                gap: "4px",
                color: "#555",
                cursor: "pointer",
              }}
            >
              <Icon style={{ fontSize: "14px" }}>content_copy</Icon>
              copy
            </button>
            {user?.userType !== "AppointmentEmployee" &&
              user?.userType !== "CallCenterEmployee" && (
                <button
                  className="btn btn-sm"
                  onClick={() => DeleteAppointment(AccSession, isDummy)}
                  title="Delete Message"
                  style={{
                    background: "#fff5f5",
                    border: "1px solid #fed7d7",
                    borderRadius: "20px",
                    padding: "4px 12px",
                    fontSize: "0.75rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    color: "#e53e3e",
                    cursor: "pointer",
                  }}
                >
                  <Icon style={{ fontSize: "14px" }}>delete</Icon>
                  Delete
                </button>
              )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      id={"AccSessionDiv" + AccSession.id}
      className={"badge AppHourSessionDiv " + DivColor}
      style={dummyStyle}
    >
      {isDummy && (
        <div
          style={{
            marginTop: "0.7em",
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "0.9em",
            color: "#595959ff",
          }}
        >
          DUMMY SESSION
        </div>
      )}

      <div className="PatientNameSection">
        <span className="PatientName">{AccSession.patientName}</span>
      </div>

      <div className="SessionInfoSection">
        <span className="SessionInfo">
          {AccSession.sessionName} (Id: {AccSession.id})
        </span>
        {AccSession.getPartition && (
          <EditPartitionButton
            AccSession={AccSession}
            AppHour={AppHour}
            isDummy={isDummy}
            setIsLoaded={setIsLoaded}
            AppDay={AppDay}
            GetAppDayById={GetAppDayById}
          >
            <div
              className="SessionInfo"
              style={{
                marginTop: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <span>
                Partition: <strong>{AccSession.getPartition?.name}</strong>
              </span>
              {!isDummy && (
                <Icon
                  style={{ fontSize: "14px", color: "#fff", marginLeft: "5px" }}
                >
                  edit
                </Icon>
              )}
            </div>
          </EditPartitionButton>
        )}
        {!isDummy && !AccSession.getPartition && (
          <div
            className="SessionInfo"
            style={{
              marginTop: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EditPartitionButton
              AccSession={AccSession}
              AppHour={AppHour}
              IsLoaded={setIsLoaded}
              AppDay={AppDay}
              GetAppDayById={GetAppDayById}
            />
          </div>
        )}
        {AccSession.getBuilding && (
          <EditBuildingButton
            AccSession={AccSession}
            AppHour={AppHour}
            IsLoaded={setIsLoaded}
            AppDay={AppDay}
            GetAppDayById={GetAppDayById}
          >
            <div
              className="SessionInfo"
              style={{
                marginTop: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <span>
                Building: <strong>{AccSession.getBuilding?.name}</strong>
              </span>
              {!isDummy && (
                <Icon
                  style={{ fontSize: "14px", color: "#fff", marginLeft: "5px" }}
                >
                  business
                </Icon>
              )}
            </div>
          </EditBuildingButton>
        )}
        {!isDummy && !AccSession.getBuilding && (
          <div
            className="SessionInfo"
            style={{
              marginTop: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EditBuildingButton
              AccSession={AccSession}
              AppHour={AppHour}
              IsLoaded={setIsLoaded}
              AppDay={AppDay}
              GetAppDayById={GetAppDayById}
            />
          </div>
        )}
        {Array.isArray(AccSession?.getSessionFacilities) &&
          AccSession.getSessionFacilities.map((sessionFacility) => (
            <span key={sessionFacility.id} className="FacilityInfo">
              {" | "} {sessionFacility?.getFacility?.name}
            </span>
          ))}
      </div>

      {!isDummy && AccSession.packageRefundableAmount < 0 && (
        <div
          className="AppHourSessionDivDetails"
          style={{ marginTop: "0.5em" }}
        >
          <span
            style={{
              color: "red",
              backgroundColor: "#ecececff",
              fontSize: "1.2em",
              padding: "10px",
              textShadow: "2px 2px 4px rgba(0, 0, 0, 0.1)",
            }}
          >
            Patient Needs To Pay Before Addmission
          </span>
        </div>
      )}

      {AccSession.availableRemainingSessions != null && (
        <div
          className="AppHourSessionDivDetails"
          style={{ marginTop: "0.5em" }}
        >
          <span
            style={{
              color:
                AccSession.availableRemainingSessions === 0
                  ? "red"
                  : AccSession.availableRemainingSessions <= 2
                    ? "yellow"
                    : "green",
              backgroundColor:
                AccSession.availableRemainingSessions === 0
                  ? "#d4edda"
                  : AccSession.availableRemainingSessions <= 2
                    ? "#000"
                    : "#ffbc05ff",
              fontSize: "1.1em",
              padding: "2px 10px",
              borderRadius: "4px",
            }}
          >
            ({AccSession.availableRemainingSessions}) Sessions Remaining
          </span>
        </div>
      )}

      <div
        className="AppHourSessionDivButtons"
        style={{
          marginTop: "1em",
          display: "flex",
          gap: "3px",
          alignItems: "center",
          padding: "2px 50px",
        }}
      >
        {user?.userType !== "Therapist" && (
            <>
              {!isDummy && (
                <AddmissionButton
                  AccSession={AccSession}
                  setIsLoaded={setIsLoaded}
                />
              )}

              {!isDummy && (
                <button
                  className="btn btn-sm btn-secondary pt-2"
                  onClick={() => EnableFacilityModal(true, AccSession)}
                >
                  <Icon fontSize="7em">P</Icon>
                </button>
              )}

              {!isDummy && (
                <button
                  className="btn btn-sm btn-primary pt-2"
                  onClick={() => {
                    updateUrlAndNavigate("#", {
                      AppDay: location.state?.AppDay,
                    });
                    GoToAccSessionDetails(AccSession);
                  }}
                >
                  <Icon fontSize="4em">I</Icon>
                </button>
              )}

              <button
                className="btn btn-sm btn-warning pt-2"
                onClick={() =>
                  setMovingSession({ ...AccSession, moveAction: "cut" })
                }
                title="Cut Session"
              >
                <Icon fontSize="4em">content_cut</Icon>
              </button>

              {isDummy && (
                <button
                  className="btn btn-sm btn-secondary pt-2"
                  onClick={() =>
                    setMovingSession({ ...AccSession, moveAction: "copy" })
                  }
                  title="Copy Session"
                  style={{ backgroundColor: "#e2e8f0", color: "#4a5568" }}
                >
                  <Icon fontSize="4em">content_copy</Icon>
                </button>
              )}

              {user?.userType !== "AppointmentEmployee" &&
                user?.userType !== "CallCenterEmployee" && (
                  <button
                    className="btn btn-sm btn-dark pt-2"
                    onClick={() => DeleteAppointment(AccSession, isDummy)}
                  >
                    <Icon fontSize="4em">delete</Icon>
                  </button>
                )}

              {!isDummy && (
                <>
                  <button
                    className="btn btn-sm btn-success pt-2"
                    onClick={refreshSessionsRemaining}
                  >
                    <Cached style={{ width: "20px", height: "20px" }} />
                  </button>

                  {!AccSession.isAbsent && user.userType !== "SubAdmin" && (
                    <button
                      className="btn btn-sm btn-info pt-2"
                      onClick={() => ToggleAbsentButtonClick(AccSession.id)}
                    >
                      A
                    </button>
                  )}

                  {AccSession.isAbsent &&
                    user.userType !== "SubAdmin" &&
                    user.userType !== "AppointmentEmployee" &&
                    user.userType !== "CallCenterEmployee" && (
                      <button
                        className="btn btn-sm btn-success pt-2"
                        onClick={() => ToggleAbsentButtonClick(AccSession.id)}
                      >
                        Un-A
                      </button>
                    )}
                </>
              )}
            </>
          )}
      </div>

      <SessionControls
        user={user}
        isTrackerEnabled={isTrackerEnabled}
        AccSession={AccSession}
        handleStart={handleStart}
        handleEnd={handleEnd}
      />

      <div className="therapistDiv" style={{ marginTop: "10px" }}>
        {canAddEntry && (
          <button
            className="btn btn-sm btn-success"
            onClick={() => routeToCreateEntry(AccSession.patientId)}
          >
            Add Entry
          </button>
        )}

        {showPatientHistory && (
          <button
            className="btn btn-sm btn-success"
            onClick={() => routeToPatientHistory(AccSession.patientId)}
          >
            Patient's History
          </button>
        )}

        {AccSession.packageId != null && user?.userType !== "Therapist" && (
          <button
            className="btn btn-sm btn-success"
            onClick={() => ShowPackageStatistics(AccSession.packageId)}
          >
            Package Statistics
          </button>
        )}

        {showWhatsapp && (
          <button
            className="btn btn-sm btn-success"
            onClick={() =>
              SendWhatsapp({
                patientId: AccSession.patientId,
                AppHour,
                AccSession,
              })
            }
          >
            <WhatsAppIcon sx={{ fontSize: 20 }} />
          </button>
        )}
      </div>

      {packageData && (
        <div
          className="package-stats-overlay"
          onClick={() => setPackageData(null)}
        >
          <div
            className="package-stats-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="close-button"
              onClick={() => setPackageData(null)}
            >
              ×
            </button>
            <h2 className="modal-title">Package Statistics</h2>
            <PackageStatistics Package={packageData} />
          </div>
        </div>
      )}
    </div>
  );
};

export default AccSessionDiv;
